import {useDndContext} from '@github-ui/drag-and-drop'
import {PlusIcon} from '@primer/octicons-react'
import {Octicon} from '@primer/react/deprecated'
import {useCallback, useMemo} from 'react'

import {useExpandedRows} from '../../../features/sub-issues/use-expanded-rows'
import {DropSide, getVerticalDropSide} from '../../../helpers/dnd-kit/drop-helpers'
import type {Row} from '../../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../../hooks/tanstack-table/providers/table-provider'
import {useKeyPress} from '../../../state-providers/keypress/key-press-provider'
import type {TableDataType} from '../table-data-type'
import {getSashPosition, isDropAfterExpandedParent} from './helpers/drop-helpers'
import {ReorderableRowData} from './reorderable-rows'
import styles from './row-reorder-sash.module.css'

type RowReorderSashProps = {
  rows: Array<Row<TableDataType>>
  isGrouped: boolean
  /** Scrolling container for table rows */
  scrollRef: React.RefObject<HTMLDivElement | null>
  /** Header height used to offset the sash position within the scrolling container. */
  headerHeight?: number
  /** Restrict the sash width to a specific width. Used in roadmap view. */
  tableWidth?: number
}

const SASH_HEIGHT = 3
const SASH_ICON_SIZE = 16
const ROW_BORDER_OFFSET = 1
const SUB_ISSUE_DEPTH_INDENT = 24
const BASE_SUB_ISSUE_INDENT = 32

export const RowReorderSash: React.FC<RowReorderSashProps> = ({
  rows,
  isGrouped,
  scrollRef,
  headerHeight,
  tableWidth,
}) => {
  const {active, over} = useDndContext()
  const {ctrlKey} = useKeyPress()
  const {getVisibleLeafColumns} = useTableInstance()
  const {isRowExpanded} = useExpandedRows()

  // Calculate the width of columns before the Title column to properly indent the sash
  const visibleColumns = getVisibleLeafColumns()
  const titleColumnIndex = visibleColumns.findIndex(c => c.id === 'Title')
  const columnsBeforeTitle = titleColumnIndex === -1 ? [] : visibleColumns.slice(0, titleColumnIndex)
  const titleColumnOffset = columnsBeforeTitle.reduce((acc, column) => acc + column.getSize(), 0)

  const lastOfTypeIndexes = useMemo(() => {
    const last: Array<number> = []
    if (isGrouped) {
      for (const row of rows) {
        const lastId = row.subRows[row.subRows.length - 1]?.original?.id
        if (lastId !== undefined) last.push(lastId)
      }
    } else {
      const lastId = rows[rows.length - 1]?.original?.id
      if (lastId !== undefined) last.push(lastId)
    }
    return last
  }, [rows, isGrouped])

  const isLastOfType = useCallback((overItemId: number) => lastOfTypeIndexes.includes(overItemId), [lastOfTypeIndexes])

  const side = active ? getVerticalDropSide(active, over) : null
  const dropY = side ? getSashPosition(over, side) : null
  const overData = over?.data.current
  const overId = ReorderableRowData.is(overData) ? overData.originalItemId : null
  const lastOfType = overId ? isLastOfType(overId) : false
  const lastOfTypeOffset = side === DropSide.AFTER && lastOfType ? -1 * SASH_HEIGHT + ROW_BORDER_OFFSET : 0

  const sashLeftOffset = useMemo(() => {
    if (!overData || !ReorderableRowData.is(overData)) {
      return 0
    }

    // When dropping AFTER an expanded parent, indent to first sub-issue position
    if (isDropAfterExpandedParent(side, overData.canExpand, isRowExpanded(overData.rowId))) {
      const effectiveDepth = (overData.subIssueDepth ?? 0) + 1
      return titleColumnOffset + BASE_SUB_ISSUE_INDENT + effectiveDepth * SUB_ISSUE_DEPTH_INDENT
    }

    // When dropping onto an existing sub-issue position
    if (overData.parentItemId !== undefined && overData.subIssueDepth !== undefined && overData.subIssueDepth > 0) {
      return titleColumnOffset + BASE_SUB_ISSUE_INDENT + overData.subIssueDepth * SUB_ISSUE_DEPTH_INDENT
    }

    return 0
  }, [overData, titleColumnOffset, side, isRowExpanded])

  const cssStyles = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    if (scrollRef?.current && dropY !== null) {
      // eslint-disable-next-line react-hooks/refs
      const rect = scrollRef.current.getBoundingClientRect()
      const headerOffset = headerHeight ?? 0

      // When dropping onto a sub-issue position, indent the sash and reduce width accordingly
      // eslint-disable-next-line react-hooks/refs
      const baseLeft = scrollRef.current.scrollLeft + sashLeftOffset
      const baseWidth = tableWidth ? tableWidth - sashLeftOffset : undefined
      const width = baseWidth !== undefined ? `${baseWidth}px` : `calc(100% - ${sashLeftOffset}px)`

      return {
        // eslint-disable-next-line react-hooks/refs
        top: scrollRef.current.scrollTop - rect.top - headerOffset + lastOfTypeOffset + dropY - ROW_BORDER_OFFSET,
        left: baseLeft,
        width,
      }
    }
    return {}
  }, [scrollRef, tableWidth, headerHeight, dropY, lastOfTypeOffset, sashLeftOffset])

  return (
    <>
      {Boolean(active && over && dropY !== null && overId && side && active.id !== over.id) && (
        <div
          style={{
            height: SASH_HEIGHT,

            ...cssStyles,
          }}
          className={styles.Box}
        >
          {isGrouped && ctrlKey ? <Octicon icon={PlusIcon} size={SASH_ICON_SIZE} className={styles.Octicon} /> : null}
        </div>
      )}
    </>
  )
}
